import { ReactElement, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import TawkMessengerReact from '@tawk.to/tawk-messenger-react';
import { CognitoUser, CognitoUserAttribute, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import { IUserRole, MfaMethod } from 'api-paths-cas';
import { UserRole } from '../../../types';
import { Method, queryData } from '../../../utilities/fetch';
import { startTimer } from '../../../utilities/helpers';
import AgentTermsModal from '../../organisms/agentTerms';
import { SpinnerModal } from '../../molecules/SpinnerModal';
import { MfaAuthOnly } from '../../organisms/mfa/mfaAuth/mfaAuthOnly';
import MfaSetupSMS from '../../organisms/mfa/mfaSetup/mfaSetupSms';
import MfaSetupTotp from '../../organisms/mfa/mfaSetup/mfaSetupTotp';
import CookieStorage from './cookie-storage';
import ForgotPassword from './forgot-password';
import ForgotPasswordSent from './forgot-password-sent';
import IdleLoggedOut from './idle-logout';
import Login from './login';
import './login.css';
import MfaSetup from './mfa-setup';
import NewPassword from './new-password';
import onForgotPassword from './onForgotPassword';
import onLoginClick, { checkMfaEnforcement } from './onLoginClick';
import onNewPasswordSubmit from './onNewPasswordSubmit';
import PageWrapper from './page-wrapper';
import { refreshSession } from './refreshSession';
import SingleSignOnCallback from './sso/login';
import SSOLogout from './sso/logout';
import { rememberNewDevice } from './rememberNewDevice';

interface AuthenticationProps {
  assetsUrl: string;
  userPoolId: string;
  appClient: string;
  mfaEnabledInConfig: boolean;
  code?: string;
  uniCode: string;
  apiUrl: string;
  children: ReactElement;
  samlEnabled?: boolean;
  isStudentApp?: boolean;
  cognitoDomain?: string;
  cognitoProviderName?: string;
  redirectUrl?: string;
  cognitoEndpoint?: string;
}

const RESEND_MFA_CODE_TIME = 120; // seconds

export interface ISessionContext {
  getIdToken?: (isTesting?: boolean) => Promise<string>;
  getAccessToken?: (isTesting?: boolean) => Promise<string>;
  onLogout?: (isTesting?: boolean) => Promise<void>;
  appRefreshSession?: () => Promise<CognitoUserSession | null>;
  isSessionValid?: boolean;
  groups?: Array<IUserRole>;
  username?: string;
  uni?: string;
  pathway?: string;
  studentNo?: string;
  pathwayAccess?: string;
  terms?: string;
  preferredUsername?: string;
  agency?: string;
  email?: string;
  partner?: string;
}

export enum AuthStage {
  'LOGIN' = 'LOGIN',
  'FORGOT' = 'FORGOT',
  'FORGOT_SENT' = 'FORGOT_SENT',
  'NEW_PASSWORD' = 'NEW_PASSWORD',
  'MFA_SETUP' = 'MFA_SETUP',
  'MFA_SETUP_TOTP' = 'MFA_SETUP_TOTP',
  'MFA_SETUP_TOTP_CODE' = 'MFA_SETUP_TOTP_CODE',
  'MFA_SETUP_SMS' = 'MFA_SETUP_SMS',
  'MFA_SETUP_SMS_CODE' = 'MFA_SETUP_SMS_CODE',
  'TOTP_CODE' = 'TOTP_CODE',
  'SMS_CODE' = 'SMS_CODE',
  'CHANGE_PASSWORD' = 'CHANGE_PASSWORD',
  'IDLE_LOGOUT' = 'IDLE_LOGOUT',
  'SSO_CALLBACK' = 'SSO_CALLBACK',
  'SSO_LOGOUT' = 'SSO_LOGOUT',
  'AGENT_UPDATED_TERMS' = 'AGENT_UPDATED_TERMS',
  'NONE' = 'NONE',
}
const env = process?.env?.NODE_ENV;

export let cookieStorage: CookieStorage;

export const SessionContext = createContext({});

const setRedirectLocalStorageItem = (isStudentApp: boolean | undefined) => {
  if (!isStudentApp && !window.location.pathname.includes('/auth/logout')) {
    window.localStorage.setItem('redirect', `${window.location.pathname}${window.location.search}`);
  }
};

function Authentication(props: AuthenticationProps): JSX.Element {
  const {
    uniCode,
    mfaEnabledInConfig,
    code,
    apiUrl,
    samlEnabled,
    assetsUrl,
    isStudentApp,
    cognitoDomain,
    cognitoProviderName,
    redirectUrl,
    userPoolId,
    appClient,
    cognitoEndpoint,
  } = props;
  const isDev =
    env === 'development' || typeof window !== 'undefined' ? window?.location?.hostname === 'localhost' : false;

  const domain = isDev ? 'localhost' : window.location.hostname;
  cookieStorage = new CookieStorage({
    domain,
    secure: true,
    sameSite: 'strict',
    path: '/',
  });

  // Why do we default this to a cookie value I can't think of a scenario where this is beneficial
  const [username, setUsername] = useState(cookieStorage?.getItem('username') ?? '');
  //const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Array<string>>([]);
  const [authStage, setAuthStage] = useState<AuthStage>(AuthStage.LOGIN);
  const [isLoading, setIsLoading] = useState(true);
  const [hashRedirect, setHashRedirect] = useState(false);
  const [user, setUser] = useState<CognitoUser>();
  const [session, setSession] = useState<CognitoUserSession>();
  const [secretCode, setSecretCode] = useState('');
  const [ssoCode, setSSOCode] = useState<string | undefined>(undefined);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [resetPasswordCode, setResetPasswordCode] = useState('');
  const [requiresUpdatedTermsAcceptance, setRequiresUpdatedTermsAcceptance] = useState(false);
  const [updatedTermsAcceptanceError, setUpdatedTermsAcceptanceError] = useState<string | undefined>(undefined);
  const [isAcceptingUpdatedTerms, setIsAcceptingUpdatedTerms] = useState(false);
  const [showTawk, setShowTawk] = useState(false);
  const [countDownTime, setCountDownTime] = useState(`00`);
  const [mfaGracePeriodExpired, setMfaGracePeriodExpired] = useState<boolean | null>(null);
  const refreshedCognitoUserRef = useRef<CognitoUser | null>(null);

  async function appRefreshSession(): Promise<CognitoUserSession | null> {
    try {
      const result = await refreshSession({
        userPoolId,
        appClient,
        username,
        endpoint: cognitoEndpoint,
      });
      if (!result || !result.session?.isValid?.()) {
        toggleTawkWidget({ hide: false });
        setIsLoading(false);
        setUser(undefined);
        setSession(undefined);
        cookieStorage.removeItem('username');
        cookieStorage.removeItem('appClient');
        cookieStorage.removeItem('apiUrl');
        cookieStorage.removeItem('uni');
        return null;
      }

      const { session, user: cognitoUser } = result;

      if (isStudentApp) {
        toggleTawkWidget({ hide: true });
      }
      refreshedCognitoUserRef.current = cognitoUser;
      setSession(session);
      setUser(cognitoUser);
      setIsLoading(false);
      cookieStorage.setItem('username', username);
      cookieStorage.setItem('appClient', appClient); // for NextJS app
      cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
      cookieStorage.setItem('uni', uniCode); // for NextJS app
      return session;
    } catch (e) {
      setIsLoading(false);
      setUser(undefined);
      setSession(undefined);
      if (e instanceof Error && e.message === 'Refresh Token has been revoked') {
        if (isStudentApp) {
          toast.error(
            `Attention: Multiple CAS Shield sessions detected. To ensure accurate system usage, you have been automatically logged out. Please return to the other session you have open to continue using CAS Shield.`,
            {
              toastId: 'reauthenticateUser',
            },
          );
        } else {
          await onLogout(true);
        }
      } else {
        setErrors([e instanceof Error ? e.message : 'An unknown error occurred']);
      }
      return null;
    }
  }

  useEffect(() => {
    // Called initially on page load / refresh
    setIsInitialLoad(true);
    const init = async () => {
      if (window.location.pathname.includes('changePassword')) {
        setIsInitialLoad(false);
        return;
      }
      if (window.location.pathname.includes('/auth/logout')) {
        // for Insights app
        await onLogout();
        setIsInitialLoad(false);
        setIsLoading(false);
        return;
      }

      if (samlEnabled) {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        setSSOCode(code || undefined);

        if (window.location.pathname.includes('/auth/sso/callback')) {
          setAuthStage(AuthStage.SSO_CALLBACK);
          setIsInitialLoad(false);
          return;
        }
      }

      if (window.location.pathname.includes('resetPassword')) {
        const urlParams = new URLSearchParams(window.location.search);
        setUsername(urlParams.get('username') || '');
        setResetPasswordCode(urlParams.get('code') || '');
        setIsInitialLoad(false);
        setIsLoading(false);
        setAuthStage(AuthStage.CHANGE_PASSWORD);
        return;
      }

      if (!session?.isValid()) {
        let refreshSession: CognitoUserSession | null = null;

        try {
          refreshSession = await appRefreshSession();
        } catch (err) {
          console.log('session refresh failed, user logged out');
        }

        // After successful session refresh, re-check MFA enforcement.
        // If MFA is not set up, checkMfaEnforcement routes to MFA_SETUP,
        // where checkMfaGracePeriod will consult the server-side skippedToday
        // flag to decide whether to re-prompt or let the user through.
        if (refreshSession?.isValid() && refreshedCognitoUserRef.current) {
          await checkMfaEnforcement({
            session: refreshSession,
            user: refreshedCognitoUserRef.current,
            mfaEnabledInConfig,
            username,
            appClient,
            apiUrl,
            setAuthStage,
            setRequiresUpdatedTermsAcceptance,
            onAuthCheckError: () => {
              // Run onLogout first — it calls setErrors([]) internally — then set our error so it wins.
              void onLogout();
              setErrors([`We couldn't verify your account. Please try again.`]);
              setIsLoading(false);
            },
          });
        }

        setIsInitialLoad(false);
        // if samlUser and refresh session didn't work, then attempt to login via SSO
        if (
          samlEnabled &&
          refreshSession === null &&
          !window.location.pathname.includes('/auth/sso/callback') &&
          authStage !== AuthStage.SSO_LOGOUT
        ) {
          // console.log('session not valid - attempting to SSO login');
          await onSingleSignOnLogin();
        }
      } else if (isStudentApp) {
        toggleTawkWidget({ hide: true });
      }
    };
    init();
  }, []);

  useEffect(() => {
    const w = window as any;
    w.Enroly = {};
    if (
      (w.location.hostname.endsWith('.net') && !w.location.hostname.includes('srp-auth-v9')) ||
      w.location.hostname.includes('localhost')
    ) {
      w.Enroly.isDevEnv = true;
    }
    if (w.Cypress) {
      w.Enroly.isTestEnv = true;
    }
  }, []);

  const checkMfaGracePeriod = useCallback(async (): Promise<void> => {
    // Fail-open policy: when the grace period status cannot be determined,
    // allow MFA skip so users are not locked out of their accounts.
    const FAIL_OPEN_DEFAULT = false;

    try {
      const token = session?.getIdToken()?.getJwtToken();
      if (!token) {
        setMfaGracePeriodExpired(FAIL_OPEN_DEFAULT);
        return;
      }
      const decodedToken: Record<string, unknown> = jwtDecode(token);
      const username = decodedToken['cognito:username'] as string;
      const pathway = (decodedToken['custom:Pathway'] as string) ?? '';
      const params = new URLSearchParams({ uni: uniCode, username });
      if (pathway) params.append('pathway', pathway);
      const response = await queryData<
        void,
        { mfaEnabled: boolean; gracePeriodExpired: boolean; skippedToday: boolean }
      >({
        uri: `/users/mfa/status?${params.toString()}`,
        idToken: token,
        domain: apiUrl,
        method: Method.Get,
      });
      if ('error' in response) {
        setMfaGracePeriodExpired(FAIL_OPEN_DEFAULT);
        return;
      }
      if (response?.gracePeriodExpired === undefined) {
        setMfaGracePeriodExpired(FAIL_OPEN_DEFAULT);
        return;
      }
      // If the user already skipped MFA setup today, don't re-prompt —
      // redirect straight to the dashboard. The skip date is stored
      // server-side in DynamoDB and cannot be tampered with from the client.
      if (response.skippedToday) {
        setAuthStage(requiresUpdatedTermsAcceptance ? AuthStage.AGENT_UPDATED_TERMS : AuthStage.NONE);
        return;
      }
      setMfaGracePeriodExpired(response.gracePeriodExpired);
    } catch {
      // Unexpected exception — apply fail-open default
      setMfaGracePeriodExpired(FAIL_OPEN_DEFAULT);
    }
  }, [session, uniCode, apiUrl, requiresUpdatedTermsAcceptance]);

  useEffect(() => {
    if (authStage === AuthStage.MFA_SETUP && session) {
      setMfaGracePeriodExpired(null);
      checkMfaGracePeriod();
    }
  }, [authStage, session, checkMfaGracePeriod]);

  // called by app before making api requests
  async function getIdToken(isTesting?: boolean): Promise<string> {
    if (isTesting) {
      return 'fakeToken';
    }

    try {
      if (session?.isValid()) {
        return session.getIdToken().getJwtToken();
      }

      const refreshSession = await appRefreshSession();
      if (!refreshSession) {
        return '';
      }

      const idToken = refreshSession.getIdToken().getJwtToken();
      return idToken;
    } catch (e) {
      console.log('error while getId Token', e);
      return '';
    }
  }
  // only user for a recorded interview
  async function getAccessToken(isTesting?: boolean): Promise<string> {
    if (isTesting) {
      return 'fakeToken';
    }
    return session?.getIdToken?.()?.getJwtToken?.() ?? '';
  }

  useEffect(() => {
    // for unsubscribe, set visa status etc. on student app
    if (code) {
      const parsedHash = atob(code);
      if (typeof parsedHash === 'string') {
        const { email, password } = JSON.parse(parsedHash);

        setUsername(email);
        setPassword(password);
      }
    }
  }, []);

  const getUser = useCallback(() => {
    if (cookieStorage && userPoolId && appClient && username) {
      const userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: appClient,
        Storage: cookieStorage,
        endpoint: cognitoEndpoint,
      });
      const newUser = new CognitoUser({
        Username: username,
        Pool: userPool!,
        Storage: cookieStorage,
      });
      setUser(newUser);
      return newUser;
    }
    return undefined;
  }, [session?.isValid(), session, username, password, cognitoEndpoint]);

  useEffect(() => {
    if (username) {
      setHashRedirect(true);
    }
  }, [username]);

  useEffect(() => {
    // for unsubscribe, set visa status etc. on student app
    const handleLoginIn = async () => {
      if (code && username && password && hashRedirect) {
        return await handleLoginClick();
      }
    };
    handleLoginIn();
  }, [hashRedirect]);

  const onSingleSignOnLogin = () => {
    if (!samlEnabled || window.location.pathname.includes('/auth/sso/callback') || session?.isValid()) {
      return;
    }

    setRedirectLocalStorageItem(isStudentApp);

    const params = new URLSearchParams();
    params.append('identity_provider', cognitoProviderName || '');
    params.append('redirect_uri', redirectUrl || '');
    params.append('response_type', 'code');
    params.append('client_id', appClient);
    params.append('scope', 'aws.cognito.signin.user.admin openid');
    const url = `${cognitoDomain}/oauth2/authorize?${params.toString()}`;

    window.location.href = url;
  };

  function handleUserNameChange(e: any) {
    setUsername(e.currentTarget.value);
    setErrors([]);
  }

  function handlePasswordChange(e: any) {
    setPassword(e.currentTarget.value);
    setErrors([]);
  }

  function handleForgotPassword(evt: any) {
    if (evt?.preventDefault) evt.preventDefault();
    try {
      const cognitoUser = getUser();
      if (!cognitoUser) {
        setErrors(['Unable to create user instance']);
        return;
      }

      onForgotPassword({
        user: cognitoUser,
        setAuthStage,
      });
    } catch (err) {
      setErrors([err instanceof Error ? err.message : 'An unknown error occurred']);
    }
  }

  async function handleLoginClick() {
    try {
      const cognitoUser = getUser();
      if (!cognitoUser) {
        setErrors(['Unable to create user instance']);
        return;
      }

      let authCheckFailed = false;
      const session = await onLoginClick({
        mfaEnabledInConfig,
        username,
        password,
        user: cognitoUser,
        setAuthStage,
        setPassword,
        setIsLoading,
        appClient: props.appClient,
        apiUrl,
        uniCode,
        setRequiresUpdatedTermsAcceptance,
        onAuthCheckError: () => {
          authCheckFailed = true;
          // Run onLogout first — it calls setErrors([]) internally — then set our error so it wins.
          void onLogout();
          setErrors([`We couldn't verify your account. Please try again.`]);
          setIsLoading(false);
        },
      });

      // When the post-login whoami check fails, skip the rest of the success path so we don't
      // re-install the (still client-valid) Cognito session and unmask the dashboard.
      if (authCheckFailed) {
        return;
      }

      if (isStudentApp) {
        toggleTawkWidget({ hide: true });
      }
      setIsLoading(false);
      setSession(session);

      if (code) {
        setAuthStage(AuthStage.NEW_PASSWORD);
      }
    } catch (e: any) {
      const error = e as AxiosError;
      if (error?.response?.data === 'Refresh Token has been revoked' && isStudentApp) {
        setErrors([
          `Attention: Multiple CAS Shield sessions detected. To ensure accurate system usage, you have been automatically logged out. Please return to the other session you have open to continue using CAS Shield.`,
        ]);
      } else {
        setErrors([e.message]);
      }
    }
  }

  async function handleNewPasswordSubmit() {
    let cognitoUser = user;
    if (window.location.pathname.includes('resetPassword')) {
      cognitoUser = getUser();
    }
    if (!cognitoUser) {
      setErrors(['Unable to create user instance']);
      return;
    }
    try {
      const session = await onNewPasswordSubmit({
        mfaEnabledInConfig,
        username,
        password,
        user: cognitoUser,
        code: resetPasswordCode,
        setPassword,
        setAuthStage,
        setIsLoading,
        appClient,
        apiUrl,
      });

      // default change password from initial login
      if (session instanceof CognitoUserSession) {
        setSession(session);
      }
      // reset password
      if (session === 'SUCCESS' && resetPasswordCode) {
        const session = await onLoginClick({
          mfaEnabledInConfig,
          username,
          password,
          user: cognitoUser,
          setAuthStage,
          setPassword,
          setIsLoading,
          appClient,
          apiUrl,
          uniCode,
          setRequiresUpdatedTermsAcceptance,
        });
        // only set session if successful / else redirecting to MFA screen / failure
        if (session) {
          setSession(session);
        }
      }
      setIsLoading(false);
    } catch (err) {
      setErrors([err instanceof Error ? err.message : 'An unknown error occurred']);
    }
  }

  async function handleSkip(): Promise<void> {
    try {
      const token = session?.getIdToken()?.getJwtToken();
      if (token) {
        const decodedToken: Record<string, unknown> = jwtDecode(token);
        const pathway = (decodedToken['custom:Pathway'] as string) ?? '';
        await queryData<unknown, { uni: string; pathway: string }>({
          uri: `/users/mfa/skip`,
          idToken: token,
          domain: apiUrl,
          method: Method.Post,
          body: { uni: uniCode, pathway },
        });
      }
    } catch {
      // Continue even if skip recording fails
    }
    if (requiresUpdatedTermsAcceptance) {
      setAuthStage(AuthStage.AGENT_UPDATED_TERMS);
    } else {
      setAuthStage(AuthStage.NONE);
    }
  }

  async function handleAcceptUpdatedTerms(): Promise<void> {
    setUpdatedTermsAcceptanceError(undefined);
    setIsAcceptingUpdatedTerms(true);

    const MAX_RETRIES = 3;
    const INITIAL_DELAY = 1000; // 1 second

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const token = session?.getIdToken()?.getJwtToken();
        const decodedToken: Record<string, unknown> | undefined = token ? jwtDecode(token) : undefined;
        const userId = decodedToken?.['cognito:username'];
        if (userId && token) {
          await queryData<{ updatedTerms: boolean }, { userId: string }>({
            uri: `/users/agents/userId/${userId}/terms`,
            idToken: token,
            domain: apiUrl,
            method: Method.Put,
            body: { updatedTerms: true },
          });
        }
        // Success - clear error and allow user to proceed
        setRequiresUpdatedTermsAcceptance(false);
        setUpdatedTermsAcceptanceError(undefined);
        setIsAcceptingUpdatedTerms(false);
        setAuthStage(AuthStage.NONE);
        return;
      } catch {
        if (attempt < MAX_RETRIES) {
          // Wait before retrying with exponential backoff
          const delay = INITIAL_DELAY * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          // All retries failed - show error and keep user on T&C screen
          setUpdatedTermsAcceptanceError('Unable to record acceptance. Please try again.');
          setIsAcceptingUpdatedTerms(false);
        }
      }
    }
  }

  function handleSelectSMS() {
    setAuthStage(AuthStage.MFA_SETUP_SMS);
  }

  function handleSelectTOTP() {
    const associateSecretCode = (secretCode: string) => {
      setSecretCode(secretCode);
      setAuthStage(AuthStage.MFA_SETUP_TOTP);
    };

    const onFailure = (e: any) => {
      console.error(e);
    };
    if (secretCode === '') {
      user?.associateSoftwareToken({ associateSecretCode, onFailure });
    } else {
      setAuthStage(AuthStage.MFA_SETUP_TOTP);
    }
  }

  function handleChooseDifferentMethod() {
    setAuthStage(AuthStage.MFA_SETUP);
  }

  function handleTOTPMfaSubmit() {
    setAuthStage(AuthStage.MFA_SETUP_TOTP_CODE);
  }

  function handleSMSMFASubmit(phone: string) {
    const onSuccess = () => {
      setAuthStage(AuthStage.MFA_SETUP_SMS_CODE);
    };

    const onFailure = (err: Error) => {
      setErrors([err.message]);
    };

    // SMS MFA fails when a user has an apostrophe in their username. The actual MFA API request
    // is successful and the session data is updated but the Device API request made after fails
    if (username.includes("'") && user) {
      const session = user.getSignInUserSession();
      setSession(session || undefined);
    }

    user?.updateAttributes(
      [
        new CognitoUserAttribute({
          Name: 'phone_number',
          Value: phone,
        }),
      ],
      (err?: Error) => {
        if (err) {
          setErrors([err.message]);
        } else {
          user.getAttributeVerificationCode('phone_number', { onSuccess, onFailure });
        }
      },
    );
  }

  function handleSMSMFACodeSubmit(code: string) {
    const onSuccess = async () => {
      const onMFAResult = async (err: Error | undefined) => {
        if (err) {
          setErrors([err.message]);
        } else {
          await handleMfaStatusUpdate(MfaMethod.SMS);
          if (requiresUpdatedTermsAcceptance) {
            setAuthStage(AuthStage.AGENT_UPDATED_TERMS);
          } else {
            setAuthStage(AuthStage.NONE);
          }
        }
      };

      user?.setUserMfaPreference(
        {
          Enabled: true,
          PreferredMfa: true,
        },
        null,
        onMFAResult,
      );
    };

    const onFailure = (err: Error) => {
      setErrors([err.message]);
    };

    user?.verifyAttribute('phone_number', code, { onSuccess, onFailure });
  }

  function handleMFASetupCodeSubmit(code: string) {
    const onSuccess = async () => {
      const onMFAResult = async (err: Error | undefined) => {
        if (err) {
          setErrors([err.message]);
        } else {
          await handleMfaStatusUpdate(MfaMethod.TOTP);
          if (requiresUpdatedTermsAcceptance) {
            setAuthStage(AuthStage.AGENT_UPDATED_TERMS);
          } else {
            setAuthStage(AuthStage.NONE);
          }
        }
      };

      user?.setUserMfaPreference(
        null,
        {
          Enabled: true,
          PreferredMfa: true,
        },
        onMFAResult,
      );
    };

    const onFailure = (err: Error) => {
      setErrors([err.message]);
    };
    user?.verifySoftwareToken(code, 'enroly', { onSuccess, onFailure });
  }

  async function onMFASuccess(session: CognitoUserSession, user: CognitoUser, rememberDevice?: boolean): Promise<void> {
    setSession(session);
    setAuthStage(AuthStage.NONE);
    if (rememberDevice) {
      const response = await rememberNewDevice(user);
      if (!response.status) {
        toast.error(`Device remembered failed. ${response.error}`);
        return;
      }
      toast.success('Device remembered successfully');
    }
  }

  function onMFAFailure(err: Error & { code?: string }): void {
    // user.sendMFACode() fails when a user has an apostrophe in their username. The SendMFA API request
    // is successful and the session data is updated but the Device API request made after fails
    if (err?.code === 'InvalidParameterException' && username.includes("'") && user) {
      const session = user.getSignInUserSession();
      setSession(session || undefined);
      setAuthStage(AuthStage.NONE);
    } else {
      setErrors([err.message]);
    }
  }

  // Called when the user has already setup their MFA method, and is authenticating
  // a future login with MFA. Not used during initial MFA setup.
  function handleTOTPCodeSubmit(code: string, rememberDevice?: boolean) {
    user?.sendMFACode(
      code,
      {
        onSuccess: (session: CognitoUserSession) => {
          onMFASuccess(session, user, rememberDevice);
        },
        onFailure: onMFAFailure,
      },
      'SOFTWARE_TOKEN_MFA',
    );
  }

  // Called when the user has already setup their MFA method, and is authenticating
  // a future login with MFA. Not used during initial MFA setup.
  function handleSMSCodeSubmit(code: string, rememberDevice?: boolean) {
    user?.sendMFACode(code, {
      onSuccess: (session: CognitoUserSession) => {
        onMFASuccess(session, user, rememberDevice);
      },
      onFailure: onMFAFailure,
    });
  }

  function handleMFAHelp() {
    window?.open('https://www.enroly.com/faqs/mfa', '_blank');
  }

  const setResendTimer = useCallback(
    (waitFor = RESEND_MFA_CODE_TIME) => {
      startTimer(waitFor, setCountDownTime, () => {
        setCountDownTime('00');
      });
    },
    [authStage],
  );

  useEffect(() => {
    if ([AuthStage.SMS_CODE, AuthStage.MFA_SETUP_SMS_CODE].includes(authStage)) {
      setResendTimer();
    }
  }, [authStage]);

  function handleResendSMSCode() {
    setResendTimer();
    const msg = 'Code sent successfully';
    user?.getAttributeVerificationCode('phone_number', {
      onSuccess: () => {
        toast.success(msg);
        return null;
      },
      onFailure: async (err: Error & { code?: string }) => {
        if (err?.message === 'User is not authenticated') {
          // With MFA setup: username/password alone doesn't give session.
          // Must complete SMS code to get user session. And Session is needed to resend code.
          // authenticateUser() auto-sends SMS
          await onLoginClick({
            mfaEnabledInConfig: false,
            username,
            password,
            user,
            appClient,
            apiUrl,
            uniCode,
            setAuthStage,
            setPassword,
            setIsLoading,
          });
          toast.success(msg);
        }
        return null;
      },
    });
  }
  const onLogout = useCallback(
    async function logout(isIdleLogout?: boolean) {
      let cognitoUser = user;
      // when logging out from Insights the user is not populated
      if (!cognitoUser) {
        cognitoUser = getUser();
      }

      cognitoUser?.signOut(() => {
        setIsLoading(false);
      });
      // reset auth vars
      setSession(undefined);
      setUsername('');
      cookieStorage.removeItem('username');
      cookieStorage.removeItem('appClient');
      cookieStorage.removeItem('apiUrl');
      cookieStorage.removeItem('uni');
      setPassword('');
      setErrors([]);
      toggleTawkWidget({ hide: false });

      if (samlEnabled) {
        setAuthStage(isIdleLogout ? AuthStage.IDLE_LOGOUT : AuthStage.SSO_LOGOUT);
        setRedirectLocalStorageItem(isStudentApp);
      } else {
        setAuthStage(isIdleLogout ? AuthStage.IDLE_LOGOUT : AuthStage.LOGIN);
        setRedirectLocalStorageItem(isStudentApp);
        window.history.replaceState(null, '', '/');
      }
      // TBC look to use globalSignOut - signs the user out of all devices
    },
    [user],
  );

  let element;
  switch (authStage) {
    case AuthStage.FORGOT:
      element = (
        <ForgotPassword
          username={username}
          errors={errors}
          onUsernameChange={handleUserNameChange}
          onForgotPasswordSubmit={handleForgotPassword}
          onLoginClick={() => setAuthStage(AuthStage.LOGIN)}
          isStudentApp={isStudentApp}
        />
      );
      break;
    case AuthStage.FORGOT_SENT:
      element = (
        <ForgotPasswordSent
          errors={errors}
          username={username}
          onForgotPasswordSubmit={handleForgotPassword}
          onLoginClick={() => setAuthStage(AuthStage.LOGIN)}
        />
      );
      break;
    case AuthStage.CHANGE_PASSWORD:
    case AuthStage.NEW_PASSWORD:
      element = (
        <NewPassword
          password={password}
          errors={errors}
          onPasswordChange={handlePasswordChange}
          onPasswordSubmit={handleNewPasswordSubmit}
        />
      );
      break;
    case AuthStage.MFA_SETUP:
      element = (
        <MfaSetup
          onSelectSMS={handleSelectSMS}
          onSelectTOTP={handleSelectTOTP}
          onSkip={handleSkip}
          canSkip={mfaGracePeriodExpired === false}
        />
      );
      break;
    case AuthStage.MFA_SETUP_TOTP:
      element = (
        <MfaSetupTotp
          totpAppLabel={`Enroly:${uniCode}`}
          secretCode={secretCode}
          onSignOut={() => null}
          onSubmit={handleTOTPMfaSubmit}
          onChooseDifferent={handleChooseDifferentMethod}
        />
      );
      break;
    case AuthStage.MFA_SETUP_TOTP_CODE:
      element = (
        <MfaAuthOnly
          uniLogo={''}
          method={MfaMethod.TOTP}
          serverErrors={errors}
          onSubmit={handleMFASetupCodeSubmit}
          onResendCode={() => null}
          resendCountdown={countDownTime}
          onAuthHelp={handleMFAHelp}
          onSignOut={() => null}
          isMobile={false}
          showRememberDevice={false}
        />
      );
      break;
    case AuthStage.MFA_SETUP_SMS:
      element = (
        <MfaSetupSMS
          onSignOut={() => null}
          onSubmit={handleSMSMFASubmit}
          onChooseDifferent={handleChooseDifferentMethod}
        />
      );
      break;
    case AuthStage.MFA_SETUP_SMS_CODE:
      element = (
        <MfaAuthOnly
          uniLogo={''}
          method={MfaMethod.SMS}
          serverErrors={errors}
          onSubmit={handleSMSMFACodeSubmit}
          onResendCode={handleResendSMSCode}
          resendCountdown={countDownTime}
          onAuthHelp={handleMFAHelp}
          onSignOut={() => null}
          isMobile={false}
          showRememberDevice={false}
        />
      );
      break;
    case AuthStage.TOTP_CODE:
      element = (
        <MfaAuthOnly
          uniLogo={''}
          method={MfaMethod.TOTP}
          serverErrors={errors}
          onSubmit={handleTOTPCodeSubmit}
          onResendCode={() => null}
          resendCountdown={countDownTime}
          onAuthHelp={handleMFAHelp}
          onSignOut={() => null}
          isMobile={false}
          showRememberDevice={true}
        />
      );
      break;
    case AuthStage.SMS_CODE:
      element = (
        <MfaAuthOnly
          uniLogo={''}
          method={MfaMethod.SMS}
          serverErrors={errors}
          onSubmit={handleSMSCodeSubmit}
          onResendCode={handleResendSMSCode}
          resendCountdown={countDownTime}
          onAuthHelp={handleMFAHelp}
          onSignOut={() => null}
          isMobile={false}
          showRememberDevice={true}
        />
      );
      break;
    case AuthStage.IDLE_LOGOUT:
      element = (
        <IdleLoggedOut
          onLoginClick={() => {
            if (samlEnabled) {
              window.location.href = '/';
            } else {
              setAuthStage(AuthStage.LOGIN);
            }
          }}
        />
      );
      break;
    case AuthStage.SSO_CALLBACK:
      element = ssoCode ? (
        <SingleSignOnCallback
          code={ssoCode}
          apiUrl={apiUrl}
          appClient={appClient}
          setAuthStage={setAuthStage}
          setUser={setUser}
          setSession={setSession}
          setUsername={setUsername}
          setIsLoading={setIsLoading}
        />
      ) : (
        <Login
          isStudentApp={isStudentApp}
          errors={['SSO authentication failed - missing authorization code']}
          username={username}
          password={password}
          samlEnabled={samlEnabled}
          onUsernameChange={handleUserNameChange}
          onPasswordChange={handlePasswordChange}
          onForgotPasswordClick={() => setAuthStage(AuthStage.FORGOT)}
          onLoginClick={handleLoginClick}
          setIsLoading={setIsLoading}
        />
      );
      break;
    case AuthStage.SSO_LOGOUT:
      element = (
        <SSOLogout
          appClient={appClient}
          cognitoDomain={cognitoDomain || ''}
          onLogout={onLogout}
          setAuthStage={setAuthStage}
        />
      );
      break;
    case AuthStage.AGENT_UPDATED_TERMS:
      element = (
        <AgentTermsModal
          onAcceptTerms={handleAcceptUpdatedTerms}
          error={updatedTermsAcceptanceError}
          isLoading={isAcceptingUpdatedTerms}
        />
      );
      break;
    default:
      element = (
        <Login
          isStudentApp={isStudentApp}
          errors={errors}
          username={username}
          password={password}
          samlEnabled={samlEnabled}
          onUsernameChange={handleUserNameChange}
          onPasswordChange={handlePasswordChange}
          onForgotPasswordClick={() => setAuthStage(AuthStage.FORGOT)}
          onLoginClick={handleLoginClick}
          setIsLoading={setIsLoading}
        />
      );
      break;
  }

  const AUTH_STAGES = [
    AuthStage.MFA_SETUP,
    AuthStage.MFA_SETUP_SMS,
    AuthStage.MFA_SETUP_SMS_CODE,
    AuthStage.MFA_SETUP_TOTP,
    AuthStage.MFA_SETUP_TOTP_CODE,
    AuthStage.SMS_CODE,
    AuthStage.TOTP_CODE,
    AuthStage.CHANGE_PASSWORD,
    AuthStage.AGENT_UPDATED_TERMS,
  ];
  const isAuthPage = AUTH_STAGES.includes(authStage);
  // Suppress rendering while the grace period API call is in-flight
  // to prevent the MFA setup screen flashing on page refresh when
  // the user has already skipped MFA setup today.
  const isMfaCheckPending = authStage === AuthStage.MFA_SETUP && mfaGracePeriodExpired === null;

  const toggleTawkWidget = ({ hide }: { hide: boolean }) => {
    toggleTawk({ hide });
    setShowTawk(!hide);
  };

  const toggleTawk = ({ hide }: { hide: boolean }) => {
    const w = window as any;
    if (w.Tawk_API) {
      if (hide) {
        if (w.Tawk_API.hideWidget) w.Tawk_API.hideWidget();
      } else {
        if (w.Tawk_API.showWidget) w.Tawk_API.showWidget();
      }
    }
  };

  const handleMfaStatusUpdate = async (mfaMethod: MfaMethod) => {
    try {
      const token = session?.getIdToken()?.getJwtToken();
      let decodedToken: any;
      if (token) {
        decodedToken = jwtDecode(token);
      }
      if (decodedToken) {
        const username = decodedToken['cognito:username'];
        const pathway = decodedToken['custom:Pathway'];
        // TODO: Give this correct request and response types
        const resp = await queryData<any, any>({
          uri: `/users/mfa/status`,
          idToken: token,
          domain: apiUrl,
          method: Method.Post,
          body: { clientId: appClient, mfaMethod, username, pathway },
        });
        return resp;
      }
    } catch (err) {
      console.log('err on handleMdaStatusUpdate', err);
    }
  };

  if (session?.isValid() && !isAuthPage && !isInitialLoad) {
    const token = session.getIdToken().getJwtToken();
    const decodedToken: any = jwtDecode(token);
    const nextUrl = window.localStorage.getItem('redirect');
    if (!isStudentApp && nextUrl) {
      window.localStorage.removeItem('redirect');
      // prevent insights loop when app not loading properly
      if (nextUrl.includes('insights')) {
        window.localStorage.setItem('redirectedToInsights', 'true');
      }

      if (window.localStorage.getItem('redirectedFromInsights')) {
        window.localStorage.removeItem('redirectedFromInsights');
      } else {
        window.location.href = `${window.location.origin}${nextUrl}`;
        return <SpinnerModal show={!!isLoading} />;
      }
    }

    return (
      <>
        <SessionContext.Provider
          value={{
            getIdToken,
            getAccessToken,
            onLogout,
            appRefreshSession,
            studentNo: decodedToken['custom:StudentNo'],
            uni: decodedToken['custom:University'] || uniCode,
            pathway: decodedToken['custom:Pathway'],
            pathwayAccess: decodedToken['custom:PathwayAccess'],
            groups: decodedToken['cognito:groups'] ?? (isStudentApp ? '' : UserRole.Unknown),
            terms: decodedToken['custom:Terms'],
            preferredUsername: decodedToken['preferred_username'],
            username: decodedToken['cognito:username'],
            agency: decodedToken['custom:Agency'],
            email: decodedToken['email'],
            partner: decodedToken['custom:Partner'],
            isSessionValid: session?.isValid(),
          }}
        >
          {props.children}
        </SessionContext.Provider>
      </>
    );
  }

  return (
    <>
      {isStudentApp && showTawk && !((window as any).isDevEnv || (window as any).isTestEnv) ? (
        <TawkMessengerReact propertyId="5ff82fa1a9a34e36b96a4dc0" widgetId="1ergnk4i0" />
      ) : null}
      <PageWrapper
        isLoading={isLoading}
        isInitialLoad={isInitialLoad || isMfaCheckPending}
        isAuthPage={isAuthPage}
        assetsUrl={assetsUrl}
        isStudentApp={isStudentApp}
      >
        {element}
      </PageWrapper>
    </>
  );
}

function useSession(): ISessionContext {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionContext Provider');
  }
  return context;
}

export { Authentication, useSession };
